import React, { useState, useEffect } from "react";
import {
  PageHeader, KPIGrid, Card, Alert,
  colors, spacing, typography, radius,
} from "../design-system";

const BASE         = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
const FEEDBACK_API = `${BASE}/feedback`;

const RATING_LABELS = { 1: "Poor", 2: "Below Average", 3: "Satisfactory", 4: "Good", 5: "Excellent" };
const RATING_COLOR  = { 1: colors.danger, 2: colors.warning, 3: colors.info, 4: colors.success, 5: colors.success };

function Section({ title, items, color }) {
  if (!items?.length) return null;
  return (
    <div>
      <p style={{ margin: `0 0 ${spacing.xs}`, fontWeight: typography.fontWeights.semibold, color, fontSize: typography.fontSizes.sm }}>
        {title}
      </p>
      <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 4 }}>
        {items.map((item, i) => (
          <li key={i} style={{ color: colors.textSecondary, fontSize: typography.fontSizes.sm, lineHeight: 1.5 }}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FeedbackCard({ fb }) {
  const rating    = fb.overall_rating;
  const ratingCol = rating ? RATING_COLOR[rating] : colors.textMuted;

  return (
    <Card style={{ marginBottom: spacing.md }}>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: spacing.md, flexWrap: "wrap", gap: spacing.sm }}>
        <div>
          <p style={{ margin: 0, fontWeight: typography.fontWeights.bold, fontSize: typography.fontSizes.base, color: colors.textPrimary }}>
            {fb.audit_type || "Audit"} — {fb.audit_date ? new Date(fb.audit_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—"}
          </p>
          {fb.iso_standard && (
            <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSizes.sm, color: colors.textSecondary }}>
              Standard: <strong>{fb.iso_standard}</strong>
              {fb.certification_number && (
                <span style={{ marginLeft: spacing.sm, fontFamily: "monospace", fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
                  {fb.certification_number}
                </span>
              )}
            </p>
          )}
          {fb.auditor_name && (
            <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSizes.sm, color: colors.textSecondary }}>
              Lead Auditor: <strong>{fb.auditor_name}</strong>
            </p>
          )}
        </div>
        {rating && (
          <div style={{
            background: ratingCol + "18",
            border: `1px solid ${ratingCol}`,
            borderRadius: radius.md,
            padding: `${spacing.xs} ${spacing.sm}`,
            textAlign: "center",
            minWidth: 90,
          }}>
            <p style={{ margin: 0, fontSize: typography.fontSizes.xl, lineHeight: 1 }}>
              {"★".repeat(rating)}{"☆".repeat(5 - rating)}
            </p>
            <p style={{ margin: 0, fontSize: typography.fontSizes.xs, color: ratingCol, fontWeight: typography.fontWeights.semibold }}>
              {RATING_LABELS[rating]}
            </p>
          </div>
        )}
      </div>

      {/* Content sections */}
      <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
        <Section title="Strengths"                         items={fb.strengths}                         color={colors.success} />
        <Section title="Areas for Improvement"             items={fb.improvements}                      color={colors.warning} />
        <Section title="Major Nonconformances"             items={fb.major_nonconformances}             color={colors.danger} />
        <Section title="Minor Nonconformances"             items={fb.minor_nonconformances}             color={colors.warning} />
        <Section title="Observations"                      items={fb.observations}                      color={colors.info} />
        <Section title="Opportunities for Improvement"     items={fb.opportunities_for_improvement}     color={colors.primary} />
      </div>

      {/* Recommendation */}
      {fb.recommendation && (
        <div style={{
          marginTop: spacing.md,
          padding: spacing.sm,
          background: colors.surface,
          borderLeft: `3px solid ${colors.primary}`,
          borderRadius: radius.sm,
        }}>
          <p style={{ margin: 0, fontSize: typography.fontSizes.sm }}>
            <strong style={{ color: colors.primary }}>Recommendation: </strong>
            <span style={{ color: colors.textPrimary }}>{fb.recommendation}</span>
          </p>
        </div>
      )}

      {/* Notes */}
      {fb.notes && (
        <p style={{ marginTop: spacing.sm, fontSize: typography.fontSizes.sm, color: colors.textSecondary, margin: `${spacing.sm} 0 0` }}>
          <em>{fb.notes}</em>
        </p>
      )}

      {/* Footer */}
      <p style={{ marginTop: spacing.md, fontSize: typography.fontSizes.xs, color: colors.textMuted, borderTop: `1px solid ${colors.border}`, paddingTop: spacing.sm, marginBottom: 0 }}>
        Recorded {new Date(fb.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
        {fb.created_by_name && ` by ${fb.created_by_name}`}
      </p>
    </Card>
  );
}

export default function MyAuditFeedback() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [msg,       setMsg]       = useState("");

  const token   = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    setLoading(true);
    fetch(`${FEEDBACK_API}/`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setFeedbacks(Array.isArray(d) ? d : []))
      .catch(() => setMsg("Failed to load feedback."))
      .finally(() => setLoading(false));
  }, []);

  const ratings   = feedbacks.map(f => f.overall_rating).filter(Boolean);
  const avgRating = ratings.length ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : "—";
  const lastAudit = feedbacks[0]?.audit_date ? new Date(feedbacks[0].audit_date).toLocaleDateString("en-GB") : "—";

  const kpis = [
    { label: "Total Feedbacks", value: feedbacks.length, icon: "📋", color: colors.primary },
    { label: "Last Audit",      value: lastAudit,        icon: "📅", color: colors.info },
    { label: "Avg Rating",      value: avgRating,        icon: "⭐", color: colors.success },
  ];

  return (
    <div>
      <PageHeader
        title="My Audit Feedback"
        subtitle="Review feedback and findings from your audits and assessments."
      />

      <KPIGrid kpis={kpis} columns={3} />

      {msg && <Alert type="error" message={msg} onClose={() => setMsg("")} />}

      {loading ? (
        <p style={{ color: colors.textMuted, textAlign: "center", padding: spacing.xl }}>Loading feedback…</p>
      ) : feedbacks.length === 0 ? (
        <Card style={{ textAlign: "center", padding: spacing.xl }}>
          <p style={{ fontSize: "2rem", margin: 0 }}>📋</p>
          <p style={{ color: colors.textSecondary, marginTop: spacing.sm }}>No audit feedback yet.</p>
          <p style={{ color: colors.textMuted, fontSize: typography.fontSizes.sm, marginTop: spacing.xs }}>
            Feedback from your audits will appear here once recorded by your auditor.
          </p>
        </Card>
      ) : (
        feedbacks.map(fb => <FeedbackCard key={fb.id} fb={fb} />)
      )}
    </div>
  );
}
